/**
 * Theme preview for design review (dev only): Alt+Shift+T, `?theme=deep|mint|aurora`, or `?dev`.
 * Production always renders the Aurora theme unless a reviewer chose another one.
 */
import { $, $$ } from '../../lib/dom';
import { STORAGE_KEYS, readStorage, writeStorage } from '../../lib/storage';
import { getActiveTheme, isTheme, setThemeClass, type Theme } from '../../lib/theme';

type ThemeChangeListener = () => void;

let onThemeChange: ThemeChangeListener = () => undefined;

/** Called after a theme change so the header, footer and page re-render with theme-specific artwork. */
export function setThemeChangeListener(listener: ThemeChangeListener): void {
  onThemeChange = listener;
}

function applyTheme(theme: Theme): void {
  setThemeClass(theme);
  writeStorage('local', STORAGE_KEYS.theme, theme);
  onThemeChange();
}

/** Theme requested in the URL or remembered from an earlier review session. */
export function initialTheme(query: URLSearchParams): Theme | null {
  const requested = query.get('theme') ?? readStorage('local', STORAGE_KEYS.theme);
  return isTheme(requested) ? requested : null;
}

export function initThemeSwitcherButtons(): void {
  $$('#themer button').forEach((button) => {
    button.setAttribute('aria-pressed', String(button.dataset.theme === getActiveTheme()));
    button.addEventListener('click', () => {
      if (isTheme(button.dataset.theme)) applyTheme(button.dataset.theme);
    });
  });
}

export function initThemeSwitcher(query: URLSearchParams): void {
  if (query.has('theme') || query.has('dev')) $('#themer')?.classList.add('show');
  document.addEventListener('keydown', (event) => {
    if (event.altKey && event.shiftKey && event.code === 'KeyT') {
      event.preventDefault();
      $('#themer')?.classList.toggle('show');
    }
  });
}
